/**
 * Format currency values
 */
export function formatCurrency(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(value)
}

/**
 * Format date values
 */
export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  })
}

type Debounced<T extends (...args: unknown[]) => unknown> = ((...args: Parameters<T>) => void) & {
  cancel: () => void
}

/**
 * Debounce with `.cancel()` — drop-in for lodash's debounce.
 * Trailing-edge only; leading/trailing options not supported.
 */
export function debounce<T extends (...args: unknown[]) => unknown>(func: T, wait: number): Debounced<T> {
  let timeout: ReturnType<typeof setTimeout> | null = null

  const debounced = (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(() => func(...args), wait)
  }

  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout)
      timeout = null
    }
  }

  return debounced as Debounced<T>
}

/**
 * Capitalize first letter of a string
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

/**
 * Generate a random ID
 */
export function generateId(): string {
  return Math.random().toString(36).substr(2, 9)
}

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 */
export function isEmpty(value: unknown): boolean {
  if (value == null) {
    return true
  }
  if (typeof value === 'string') {
    return value.trim() === ''
  }
  if (Array.isArray(value)) {
    return value.length === 0
  }
  if (typeof value === 'object') {
    return Object.keys(value).length === 0
  }
  return false
}

/**
 * Sleep utility for async operations
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Get reCAPTCHA headers for API requests
 */
export function getReCaptchaHeaders(params: Record<string, any>): Record<string, string> {
  const headers: Record<string, string> = {}

  if ('recaptcha' in params && params.recaptcha) {
    headers['recaptcha'] = params['recaptcha']
    headers['recaptcha-action'] = params['recaptcha_action'] || 'submit'

    // Remove from params to avoid sending in request body
    delete params['recaptcha']
    delete params['recaptcha_action']
  }

  return headers
}
export function getTrimmedValue(value: string | null | undefined): string {
  return (value || '')
    .toLowerCase()
    .replace(/-editor/g, '')
    .replace(/-updated/g, '')
    .replace(/updated/g, '')
    .replace(/editor/g, '')
    .replace(/\([^)]*\)/g, '')
    .trim()
}

export function stopMouseflowRecording(): void {
  const mf = (window as any).mouseflow
  if (mf && mf.isRecording()) {
    mf.stop()
  }
}

export function prepareCartItems(cart: any[], params: { products: any[]; checkout: boolean }): any[] {
  return params.products
    .filter(product => {
      const campaignProduct = product.campaign_product || product
      const cartProduct = cart.find((item: any) => item.campaign_product.id === campaignProduct.id)
      if (cartProduct && campaignProduct.max_quantity >= cartProduct.quantity) {
        return false
      }
      return true
    })
    .map(product => {
      const campaignProduct = product.campaign_product || product
      return {
        quantity: 1,
        variant: product.variant,
        buy_now_clicked: params.checkout,
        campaign_product: campaignProduct,
      }
    })
}
